import { __assign, __decorate, __extends, __metadata, __rest } from "tslib";
import * as React from 'react';
import { observer } from 'mobx-react';
import { GridContext } from './Grid';
import { Misc, Components, Utils } from '@singularsystems/neo-core';
import Column from './Column';
import NeoButton from '../Button';
import { StateObserver } from '../StateObserver';
import ComponentBase from '../ComponentBase';
var Row = /** @class */ (function (_super) {
    __extends(Row, _super);
    function Row(props) {
        var _this = _super.call(this, props) || this;
        _this.onExpandClick = _this.onExpandClick.bind(_this);
        return _this;
    }
    Row.prototype.setGridContext = function () {
        var _this = this;
        this.context.inFirstRowInGroup = false;
        if (!this.context.doneStaticSetup) {
            this.context.doneStaticSetup = true;
            React.Children.forEach(this.props.children, function (child) {
                if (child) {
                    var childProps = child.props;
                    _this.context.columnCount += (childProps ? childProps.colSpan || 1 : 1);
                    if (childProps && (childProps.sum || childProps.footerContent)) {
                        _this.context.hasSummaries = true;
                    }
                }
            });
        }
    };
    Row.prototype.onFirstRender = function () {
        var _this = this;
        this.section = this.context.section;
        this.expandProperty = this.context.inFirstRowInGroup ? this.context.expandProperty : undefined;
        this.firstRowInGroup = this.context.inFirstRowInGroup;
        if (this.expandProperty && this.section === "body") {
            this.stateObserver = new StateObserver(this, function () {
                return { isExpanded: _this.expandProperty.value };
            });
        }
    };
    Row.prototype.componentWillUnmount = function () {
        if (this.stateObserver)
            this.stateObserver.dispose();
    };
    Row.prototype.onExpandClick = function () {
        if (this.expandProperty) {
            this.expandProperty.value = !this.expandProperty.value;
        }
    };
    Row.prototype.render = function () {
        var _this = this;
        _super.prototype.onRender.call(this);
        var _a = this.props, showInHeader = _a.showInHeader, onHeaderClick = _a.onHeaderClick, domProps = __rest(_a, ["showInHeader", "onHeaderClick"]);
        var inBody = this.section === "body";
        if (this.stateObserver)
            this.stateObserver.observe();
        if (!this.context.dataView) {
            throw "NeoGrid.Row cannot be used outside of a NeoGrid.";
        }
        var expandButtonOptions = __assign({}, Misc.Settings.grid.expandButton);
        if (inBody && this.expandProperty) {
            expandButtonOptions.onClick = this.onExpandClick;
            expandButtonOptions.icon = this.expandProperty.value ? Misc.Settings.icons.expanded : Misc.Settings.icons.collapsed;
        }
        if (!inBody) {
            if (onHeaderClick) {
                domProps.onClick = function (e) { return onHeaderClick(_this.section, e); };
            }
            else {
                domProps.onClick = undefined;
            }
        }
        var output = ((this.props.showInHeader || inBody) &&
            React.createElement("tr", __assign({}, domProps),
                this.expandProperty && this.context.expandAll === undefined &&
                    React.createElement(Column, { className: "neo-grid-expand-column" }, this.firstRowInGroup && inBody && this.context.hideExpandButton !== true && this.expandProperty.value !== null &&
                        React.createElement(NeoButton, __assign({}, expandButtonOptions))),
                this.props.children));
        this.setGridContext();
        return output;
    };
    Row.contextType = GridContext;
    Row.defaultProps = {
        showInHeader: true
    };
    return Row;
}(ComponentBase));
export default Row;
var ChildRow = /** @class */ (function (_super) {
    __extends(ChildRow, _super);
    function ChildRow(props) {
        var _this = _super.call(this, props) || this;
        _this.wasExpanded = false;
        _this.lastExpandValue = null;
        _this.containerDiv = React.createRef();
        _this.afterTransition = _this.afterTransition.bind(_this);
        _this.afterRender = _this.afterRender.bind(_this);
        return _this;
    }
    Object.defineProperty(ChildRow.prototype, "isExpanded", {
        get: function () {
            if (this.props.isExpanded !== undefined) {
                return this.props.isExpanded;
            }
            return !this.expandProperty || this.expandProperty.value;
        },
        enumerable: false,
        configurable: true
    });
    ChildRow.prototype.onFirstRender = function () {
        this.expandProperty = this.context.expandProperty;
        this.inBody = this.context.inBody;
    };
    ChildRow.prototype.afterRender = function (isInitial) {
        if (this.inBody) {
            var expand = this.isExpanded;
            if (!isInitial && expand !== this.lastExpandValue && this.context.expandAll === undefined) {
                this.setHeight(expand);
            }
            this.lastExpandValue = expand;
        }
    };
    ChildRow.prototype.afterTransition = function () {
        if (this.containerDiv.current) {
            this.containerDiv.current.style.maxHeight = 'none';
            this.containerDiv.current.style.overflow = "visible";
            if (this.wasExpanded && !this.isExpanded) {
                // When collapsing, the child element must be removed after animation.
                this.wasExpanded = false;
                this.forceUpdate();
            }
        }
    };
    ChildRow.prototype.setHeight = function (expand) {
        var childDiv = this.containerDiv.current;
        if (childDiv) {
            var transitionDuration = window.getComputedStyle(childDiv).transitionDuration;
            if (transitionDuration) {
                // If expand / collapse has a transition, reset the max height after the transition ends.
                // Subtract 50ms on collapse to prevent wierd chrome padding animation issue.
                var delay = parseFloat(transitionDuration) * 1000;
                setTimeout(this.afterTransition, delay - (expand ? 0 : 50));
            }
            else {
                this.afterTransition();
            }
            childDiv.style.maxHeight = (!expand ? childDiv.scrollHeight : 0) + 'px';
            childDiv.style.overflow = "hidden";
            Components.AnimationHelper.triggerReflow(childDiv);
            childDiv.style.maxHeight = (expand ? childDiv.scrollHeight : 0) + 'px';
        }
    };
    ChildRow.prototype.render = function () {
        var _a;
        this.onRender();
        var _b = this.props, isExpanded = _b.isExpanded, isFullWidth = _b.isFullWidth, childCellProps = _b.childCellProps, domProps = __rest(_b, ["isExpanded", "isFullWidth", "childCellProps"]);
        isExpanded = this.inBody && ((_a = this.context.expandAll) !== null && _a !== void 0 ? _a : this.isExpanded);
        if (this.context.expandAll !== undefined) {
            // Prevent animation on whole table expand change.
            this.wasExpanded = false;
        }
        else if (isExpanded) {
            this.wasExpanded = true;
        }
        domProps.className = Utils.joinWithSpaces(domProps.className, "neo-child-row");
        if (childCellProps === undefined) {
            childCellProps = {};
        }
        childCellProps.className = Utils.joinWithSpaces(childCellProps.className, "neo-child-row-cell");
        var showExpandButtonColumn = this.context.expandProperty && this.context.expandAll === undefined && !this.props.isFullWidth;
        var colSpan = this.context.columnCount + (showExpandButtonColumn ? 0 : 1);
        return (this.inBody && (isExpanded || this.wasExpanded) &&
            React.createElement("tr", __assign({}, domProps),
                showExpandButtonColumn && React.createElement("td", null),
                React.createElement("td", __assign({}, childCellProps, { colSpan: colSpan }),
                    React.createElement("div", { ref: this.containerDiv, className: "neo-child-row-container" }, this.props.children))));
    };
    ChildRow.contextType = GridContext;
    ChildRow = __decorate([
        observer,
        __metadata("design:paramtypes", [Object])
    ], ChildRow);
    return ChildRow;
}(ComponentBase));
export { ChildRow };
var RowGroup = /** @class */ (function (_super) {
    __extends(RowGroup, _super);
    function RowGroup() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    RowGroup.prototype.render = function () {
        this.context.expandProperty = this.props.expandProperty;
        this.context.inFirstRowInGroup = true;
        this.context.hideExpandButton = this.props.hideExpandButton;
        return this.props.children;
    };
    RowGroup.contextType = GridContext;
    return RowGroup;
}(React.Component));
export { RowGroup };
